/* Audit Assistant — scrollable conversation area.
 * Shows the welcome block + suggestion chips when empty, then the
 * message list + typing indicator. Auto-scrolls to the newest content. */

import { useEffect, useRef } from 'react';
import type { JSX } from 'react';
import type { ChatMessage } from '../../hooks/useMockChat';
import { ChatBubble } from './ChatBubble';
import { TypingIndicator } from './TypingIndicator';
import { SuggestionChips } from './SuggestionChips';

interface ChatWindowProps {
  messages: ChatMessage[];
  typing: boolean;
  onPick: (prompt: string) => void;
  onRegenerate: (id: string) => void;
}

const WELCOME_ITEMS = [
  'Due Diligence findings',
  'Risk observations',
  'SPA reviews',
  'Investment summaries',
  'Financial metrics',
  'Report navigation',
  'Audit recommendations',
];

export function ChatWindow({ messages, typing, onPick, onRegenerate }: ChatWindowProps): JSX.Element {
  const endRef = useRef<HTMLDivElement | null>(null);
  const last = messages[messages.length - 1];
  const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
  const lastAssistantId = lastAssistant?.id;
  // Follow-ups belong to the newest answer only — older ones would be stale
  // suggestions for a conversation that has already moved on.
  const followUps =
    !typing && last?.role === 'assistant' && last.followUps?.length ? last.followUps : null;

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, typing]);

  return (
    <div className="aa-window" role="log" aria-label="Conversation" aria-live="polite">
      {messages.length === 0 && (
        <div className="aa-welcome">
          <div className="aa-welcome-card">
            <p className="aa-welcome-hi">Hello! I'm <strong>Audit Assistant</strong>.</p>
            <p className="aa-welcome-sub">I can help you with</p>
            <ul className="aa-welcome-list">
              {WELCOME_ITEMS.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
            <p className="aa-welcome-ask">Ask me anything.</p>
          </div>
          <SuggestionChips onPick={onPick} disabled={typing} />
        </div>
      )}

      {messages.map((m) => (
        <ChatBubble
          key={m.id}
          message={m}
          onRegenerate={onRegenerate}
          canRegenerate={!typing && m.role === 'assistant' && m.id === lastAssistantId}
        />
      ))}

      {followUps && (
        <SuggestionChips prompts={followUps} onPick={onPick} label="Follow-up questions" />
      )}

      {typing && <TypingIndicator />}
      <div ref={endRef} />
    </div>
  );
}
